// Shared leaf bits + style constants for the search components. Ported from
// fql-search.jsx (Check, Empty, LoadingRows, Badge, CountPill + rowStyle /
// rangeInput / listScroll).
import { h } from 'vue'

export const mono = 'var(--p-font-mono, ui-monospace, monospace)'

export function rowStyle(activeRow) {
  return {
    width: '100%',
    textAlign: 'left',
    border: 0,
    borderRadius: '8px',
    padding: '7px 10px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    background: activeRow ? 'var(--p-overlay-popover-background)' : 'transparent',
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
  }
}

export const rangeInput = {
  width: '100%',
  minWidth: 0,
  flex: 1,
  padding: '6px 8px',
  borderRadius: '7px',
  border: '1px solid var(--p-content-border-color)',
  background: 'var(--surface-ground, transparent)',
  color: 'var(--p-text-color)',
  fontSize: '12.5px',
  fontFamily: 'var(--p-font-mono, monospace)',
  outline: 'none',
}

// Lists cap so the 11th row peeks through as a silent "scroll for more"
// affordance: 10 full rows + ~40% of the 11th.
const SCROLL_ROW_H = 31
export const listScroll = { maxHeight: `${SCROLL_ROW_H * 10 + 12}px`, overflowY: 'auto', overscrollBehavior: 'contain' }

export const Check = {
  name: 'Check',
  props: { on: { type: Boolean, default: false } },
  setup(props) {
    return () =>
      h(
        'span',
        {
          style: {
            width: '16px',
            height: '16px',
            borderRadius: '5px',
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: props.on ? 'var(--p-primary-500)' : 'transparent',
            border: `1.5px solid ${props.on ? 'var(--p-primary-600)' : 'var(--p-content-border-color)'}`,
            color: 'var(--p-primary-950)',
          },
        },
        props.on ? [h('i', { class: 'pi pi-check', style: { fontSize: '9px' } })] : [],
      )
  },
}

export const Empty = {
  name: 'Empty',
  props: { label: { type: String, default: '' } },
  setup(props) {
    return () => h('div', { style: { padding: '12px 12px', fontSize: '12.5px', color: 'var(--p-surface-400)', textAlign: 'center' } }, props.label)
  },
}

export const LoadingRows = {
  name: 'LoadingRows',
  setup() {
    return () =>
      h('div', { style: { padding: '6px 4px' } }, [
        ...[0, 1, 2].map((i) =>
          h('div', { key: i, style: { display: 'flex', alignItems: 'center', gap: '9px', padding: '8px 10px' } }, [
            h('span', { class: 'fql-shimmer', style: { width: '16px', height: '16px', borderRadius: '5px' } }),
            h('span', { class: 'fql-shimmer', style: { height: '11px', borderRadius: '6px', width: `${55 - i * 12}%` } }),
          ]),
        ),
        h('div', { style: { display: 'flex', alignItems: 'center', gap: '7px', padding: '2px 12px 6px', fontSize: '11px', color: 'var(--p-surface-400)' } }, [
          h('i', { class: 'pi pi-spin pi-spinner', style: { fontSize: '11px' } }),
          ' Querying backend…',
        ])
      ])
  },
}

export const Badge = {
  name: 'Badge',
  props: { n: { default: 0 } },
  setup(props) {
    return () =>
      h(
        'span',
        {
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '16px',
            height: '16px',
            padding: '0 5px',
            borderRadius: '999px',
            background: 'var(--p-primary-500)',
            color: 'var(--p-primary-950)',
            fontSize: '10px',
            fontWeight: 800,
            fontFamily: mono,
          },
        },
        String(props.n),
      )
  },
}

export const CountPill = {
  name: 'CountPill',
  props: { sel: { default: 0 }, total: { default: 0 } },
  setup(props) {
    return () =>
      h(
        'span',
        {
          style: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '16px',
            padding: '0 6px',
            borderRadius: '999px',
            background: props.sel > 0 ? 'var(--p-primary-500)' : 'var(--p-overlay-popover-background)',
            color: props.sel > 0 ? 'var(--p-primary-950)' : 'var(--p-surface-400)',
            fontSize: '10px',
            fontWeight: 800,
            fontFamily: mono,
            fontVariantNumeric: 'tabular-nums',
          },
        },
        `${props.sel}/${props.total}`,
      )
  },
}
